import React, { useState, useEffect } from 'react';
import { User, MapPin, Briefcase, Edit2, X, Check, Upload, Shield, FileText, ShieldCheck } from 'lucide-react';
import api, { verificationAPI, authAPI } from '../utils/api';
import { useSimpleAuthEnabled } from '../hooks/useSimpleAuthEnabled';
import DeleteAccountModal from './DeleteAccountModal';
import { formatRelativeTime } from '../utils/timeUtils';
import { toPlainExcerpt } from '../utils/postContent';

interface ExtractedFields {
  name?: string;
  licenseType?: string;
  licenseNumber?: string;
  documentNumber?: string;
  issueDate?: string;
  specialty?: string;
}

interface ProfilePageProps {
  userId: number;
  isOwnProfile: boolean;
  onClose: () => void;
}

interface ProfileData {
  user: {
    id: number;
    email: string;
    name: string;
    hospital?: string;
    specialization?: string;
    bio?: string;
    profileImage?: string;
    isVerifiedDoctor: boolean;
    license_verified?: boolean;
    verification_status?: string;
    verification_score?: number;
    createdAt: string;
    [key: string]: any;
  };
  stats: {
    postCount: number;
    likesReceived: number;
  };
  recentPosts: any[];
}

// PG(camelCase)/SQLite(snake_case) dual-read 헬퍼
const uf = (u: any, snake: string, camel: string): any => {
  if (!u) return undefined;
  return u[snake] !== undefined ? u[snake] : u[camel];
};

export const ProfilePage: React.FC<ProfilePageProps> = ({ userId, isOwnProfile, onClose }) => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    hospital: '',
    specialization: '',
    bio: ''
  });
  const [doctorLicenseFile, setDoctorLicenseFile] = useState<File | null>(null);
  const [specialistLicenseFile, setSpecialistLicenseFile] = useState<File | null>(null);
  const [uploadingLicense, setUploadingLicense] = useState(false);
  const simpleAuthEnabled = useSimpleAuthEnabled();
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);

  // 간편인증(PASS)
  const [passBirth, setPassBirth] = useState('');
  const [passPhone, setPassPhone] = useState('');
  const [passProvider, setPassProvider] = useState('pass');
  const [passTelecom, setPassTelecom] = useState('');
  const [passRrn7, setPassRrn7] = useState('');
  const [passConsent, setPassConsent] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [passMessage, setPassMessage] = useState('');

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/profile/${userId}`);
      setProfile(res.data);
      setEditForm({
        name: res.data.user.name || '',
        hospital: res.data.user.hospital || '',
        specialization: res.data.user.specialization || '',
        bio: res.data.user.bio || ''
      });
    } catch (error) {
      console.error('프로필 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'doctor' | 'specialist') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'doctor') {
        setDoctorLicenseFile(file);
      } else {
        setSpecialistLicenseFile(file);
      }
    }
  };

  // 자동검증 직후 stale JWT 갱신
  const handleAutoVerified = async () => {
    try {
      const rt = localStorage.getItem('refreshToken');
      if (rt) {
        const res = await authAPI.refreshToken(rt);
        if (res.data?.token) localStorage.setItem('token', res.data.token);
      }
      const prof = await authAPI.getProfile();
      const freshUser = prof.data?.user || prof.data;
      if (freshUser) localStorage.setItem('user', JSON.stringify(freshUser));
    } catch {
      /* 다음 로그인 시 반영됨 */
    }
    window.location.reload();
  };

  const fmtExtracted = (label: string, f: ExtractedFields): string => {
    const lines: string[] = [];
    if (f.licenseNumber) lines.push(`${label === '의사면허증' ? '면허번호' : '자격번호'}: ${f.licenseNumber}`);
    if (f.documentNumber) lines.push(`문서확인번호: ${f.documentNumber}`);
    if (f.issueDate) lines.push(`발급일: ${f.issueDate}`);
    if (f.specialty) lines.push(`전문과목: ${f.specialty}`);
    return lines.length ? `\n\n📄 ${label} 정보 추출:\n${lines.join('\n')}` : '';
  };

  const handleSaveProfile = async () => {
    try {
      setUploadingLicense(true);

      // 1) 텍스트 필드 저장
      await api.put('/profile', { ...editForm });

      // 2) 면허 이미지는 검증 OCR 파이프라인으로 업로드(추출+pending 라우팅, 서버에서 주민번호 마스킹)
      let message = '프로필이 수정되었습니다!';
      let extractedAny = false;

      if (doctorLicenseFile) {
        const res = await verificationAPI.ocr(doctorLicenseFile, 'doctor');
        const ex: ExtractedFields = res.data.extracted || {};
        const m = fmtExtracted('의사면허증', ex);
        if (m) { message += m; extractedAny = true; }
      }

      if (specialistLicenseFile) {
        const res = await verificationAPI.ocr(specialistLicenseFile, 'specialist');
        const ex: ExtractedFields = res.data.extracted || {};
        const m = fmtExtracted('전문의자격증', ex);
        if (m) { message += m; extractedAny = true; }
      }

      if (extractedAny) {
        message += '\n\n추출된 정보는 관리자 승인 후 인증 배지로 반영됩니다.';
      }

      await fetchProfile();
      setIsEditing(false);
      setDoctorLicenseFile(null);
      setSpecialistLicenseFile(null);
      alert(message);
    } catch (error: any) {
      console.error('프로필 수정 실패:', error);
      alert(error.response?.data?.error || '프로필 수정에 실패했습니다.');
    } finally {
      setUploadingLicense(false);
    }
  };

  const handlePassVerify = async () => {
    setPassMessage('');
    if (!profile?.user.name) {
      setPassMessage('이름 정보가 필요합니다.');
      return;
    }
    if (!/^\d{8}$/.test(passBirth)) {
      setPassMessage('생년월일은 YYYYMMDD 8자리로 입력해주세요.');
      return;
    }
    if (!/^\d{7}$/.test(passRrn7)) {
      setPassMessage('주민등록번호 뒤 7자리를 입력해주세요.');
      return;
    }
    if (!passConsent) {
      setPassMessage('본인확인 및 면허정보 조회에 동의해주세요.');
      return;
    }
    setPassLoading(true);
    try {
      const init = await verificationAPI.passInit({
        provider: passProvider,
        name: profile.user.name,
        birthDate: passBirth,
        phone: passPhone || undefined,
        telecom: passProvider === 'pass' && passTelecom ? passTelecom : undefined,
        rrn7: passRrn7,
      });
      // 주민번호 뒤 7자리는 전송 후 메모리에서 즉시 폐기 (재요청 시 재입력).
      setPassRrn7('');
      setPassMessage('PASS 앱에서 본인인증을 승인한 뒤 잠시만 기다려주세요...');

      const complete = await verificationAPI.passComplete(init.data.txId);
      const { verificationStatus, score, requiresRelogin } = complete.data;

      if (verificationStatus === 'verified') {
        setPassMessage(`✓ 자동 검증 완료 (신뢰도 ${score}/100). 인증 정보를 갱신합니다...`);
        if (requiresRelogin) {
          await handleAutoVerified();
          return;
        }
        await fetchProfile();
      } else if (verificationStatus === 'pending') {
        setPassMessage(`검증 신청이 접수되었습니다 (신뢰도 ${score}/100). 관리자 검토 후 인증 배지가 부여됩니다.`);
        await fetchProfile();
      } else {
        setPassMessage('검증이 반려되었습니다. 면허 정보를 확인 후 다시 시도해주세요.');
      }
    } catch (error: any) {
      setPassMessage(error.response?.data?.error || '간편인증에 실패했습니다.');
    } finally {
      setPassLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-gray-600">프로필 로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-red-600">프로필을 불러올 수 없습니다.</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            닫기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">프로필</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Profile Content */}
        <div className="p-6">
          {/* Profile Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center text-4xl mr-6">
                {profile.user.profileImage ? (
                  <img src={profile.user.profileImage} alt={profile.user.name} className="w-24 h-24 rounded-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-gray-400" />
                )}
              </div>
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="text-2xl font-bold text-gray-900 border-b-2 border-gray-900 focus:outline-none mb-2"
                    placeholder="이름"
                  />
                ) : (
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{profile.user.name}</h3>
                )}
                <div className="flex flex-wrap gap-2">
                  {profile.user.isVerifiedDoctor && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      ✓ 인증 의사
                    </span>
                  )}
                  {profile.user.license_verified && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                      <Shield className="w-4 h-4 mr-1" />
                      면허 등록
                    </span>
                  )}
                  {isOwnProfile && !profile.user.isVerifiedDoctor && (() => {
                    const status = uf(profile.user, 'verification_status', 'verificationStatus');
                    if (status === 'pending') {
                      return (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                          ⏳ 검증 검토 중
                        </span>
                      );
                    }
                    if (status === 'rejected') {
                      return (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                          ✕ 검증 반려됨
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>

            {isOwnProfile && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-700"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                프로필 편집
              </button>
            )}

            {isEditing && (
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveProfile}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  저장
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditForm({
                      name: profile.user.name || '',
                      hospital: profile.user.hospital || '',
                      specialization: profile.user.specialization || '',
                      bio: profile.user.bio || ''
                    });
                  }}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  취소
                </button>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="space-y-4 mb-6">
            {isEditing ? (
              <>
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-3" />
                  <input
                    type="text"
                    value={editForm.hospital}
                    onChange={(e) => setEditForm({ ...editForm, hospital: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                    placeholder="병원명"
                  />
                </div>
                <div className="flex items-center text-gray-600">
                  <Briefcase className="w-5 h-5 mr-3" />
                  <input
                    type="text"
                    value={editForm.specialization}
                    onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
                    placeholder="전문분야"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">소개</label>
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 h-24"
                    placeholder="자기소개를 입력하세요 (최대 500자)"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">{editForm.bio.length}/500</p>
                </div>

                {/* 면허증 업로드 섹션 */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <Shield className="w-5 h-5 mr-2" />
                      면허증 업로드 및 자동 인식
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      의사면허증 또는 전문의자격증을 업로드하시면 <strong>AI가 자동으로 문서번호와 면허번호를 추출</strong>합니다.
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1 mb-2">
                      <li>✓ 자동 OCR 인식으로 정보 추출</li>
                      <li>✓ 관리자 승인 후 인증 배지 부여</li>
                      <li>✓ 발급일 90일 이내 문서 진위확인 가능</li>
                    </ul>
                    {/* 처리방침과 일치: 면허증 이미지는 외부 AI(Google Gemini)로 판독하고 검증 결정 즉시 삭제한다 */}
                    <p className="text-xs text-gray-700 bg-white border border-gray-200 rounded-md px-3 py-2 mb-2">
                      주민등록번호 뒷자리는 가리고 올려 주세요. 면허증 이미지는 외부 AI(Google)로 판독하며, 검증이 끝나면 바로 삭제합니다. 
                      <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline">개인정보 처리방침</a>
                    </p>
                    <p className="text-xs text-gray-600">
                      📄 발급처: <a 
                        href="https://lic.mohw.go.kr" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-900"
                      >
                        보건복지부 면허민원
                      </a> | 
                      <a 
                        href="https://lic.mohw.go.kr/instt/docuNo_check.do?MENU_ID=D-01-01" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-900 ml-2"
                      >
                        진위확인
                      </a>
                    </p>
                  </div>

                  {/* 간편인증은 실제 면허조회 수단이 연결된 경우에만 — 아니면 주민번호를 받지 않는다 */}
                  {simpleAuthEnabled && (
                  <>
                  {/* 간편인증(PASS) 즉시 검증 */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                      <ShieldCheck className="w-5 h-5 mr-2" />
                      간편인증으로 즉시 검증 (권장)
                    </h4>
                    <p className="text-sm text-purple-800 mb-3">
                      간편인증(PASS/카카오/네이버 등)으로 보건복지부 면허정보와 자동 대조합니다. 대부분 즉시 인증됩니다.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <select
                        value={passProvider}
                        onChange={(e) => setPassProvider(e.target.value)}
                        className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                      >
                        <option value="pass">통신사 PASS</option>
                        <option value="kakao">카카오톡</option>
                        <option value="naver">네이버</option>
                        <option value="toss">토스</option>
                        <option value="kb">KB모바일</option>
                        <option value="samsung">삼성패스</option>
                        <option value="shinhan">신한</option>
                        <option value="hana">하나</option>
                        <option value="nh">NH</option>
                      </select>
                      {passProvider === 'pass' ? (
                        <select
                          value={passTelecom}
                          onChange={(e) => setPassTelecom(e.target.value)}
                          className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                        >
                          <option value="">통신사 선택</option>
                          <option value="0">SKT</option>
                          <option value="1">KT</option>
                          <option value="2">LG U+</option>
                          <option value="3">SKT 알뜰폰</option>
                          <option value="4">KT 알뜰폰</option>
                          <option value="5">LG U+ 알뜰폰</option>
                        </select>
                      ) : (
                        <div />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={passBirth}
                        onChange={(e) => setPassBirth(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        placeholder="생년월일 (YYYYMMDD)"
                        className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                      <input
                        type="tel"
                        value={passPhone}
                        onChange={(e) => setPassPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="휴대폰번호 (선택)"
                        className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-purple-900 font-medium whitespace-nowrap">주민번호</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={passBirth.slice(0, 6)}
                        readOnly
                        tabIndex={-1}
                        placeholder="앞 6자리"
                        className="w-24 px-3 py-2 border border-purple-200 bg-purple-50 text-purple-400 rounded-md text-sm text-center"
                      />
                      <span className="text-purple-400">-</span>
                      <input
                        type="password"
                        inputMode="numeric"
                        autoComplete="off"
                        value={passRrn7}
                        onChange={(e) => setPassRrn7(e.target.value.replace(/\D/g, '').slice(0, 7))}
                        placeholder="뒤 7자리"
                        className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm tracking-widest"
                      />
                    </div>
                    <p className="text-[11px] text-purple-600 mb-2">
                      주민등록번호는 본인확인을 위해 암호화 전송되며 서버에 저장하지 않습니다.
                    </p>
                    <label className="flex items-start gap-2 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={passConsent}
                        onChange={(e) => setPassConsent(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span className="text-xs text-purple-800">
                        본인확인 및 보건복지부 의료인 면허정보 조회에 동의합니다.
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={handlePassVerify}
                      disabled={passLoading}
                      className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {passLoading ? 'PASS 인증 진행 중...' : '간편인증으로 검증하기'}
                    </button>
                    {passMessage && (
                      <p className="text-xs text-purple-800 mt-2 whitespace-pre-wrap">{passMessage}</p>
                    )}
                  </div>

                  <div className="flex items-center my-3">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="px-3 text-xs text-gray-400">또는 면허증 이미지로 신청</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>

                  </>
                  )}

                  {/* 의사면허증 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FileText className="inline w-4 h-4 mr-1" />
                      의사면허증
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'doctor')}
                        className="hidden"
                        id="profile-doctor-license"
                      />
                      <label
                        htmlFor="profile-doctor-license"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        {doctorLicenseFile ? (
                          <span className="text-sm text-green-600 font-medium">
                            ✓ {doctorLicenseFile.name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">
                            클릭하여 파일 선택
                          </span>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* 전문의자격증 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FileText className="inline w-4 h-4 mr-1" />
                      전문의자격증 (선택)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'specialist')}
                        className="hidden"
                        id="profile-specialist-license"
                      />
                      <label
                        htmlFor="profile-specialist-license"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        {specialistLicenseFile ? (
                          <span className="text-sm text-green-600 font-medium">
                            ✓ {specialistLicenseFile.name}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">
                            클릭하여 파일 선택
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {profile.user.hospital && (
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-5 h-5 mr-3" />
                    <span>{profile.user.hospital}</span>
                  </div>
                )}
                {profile.user.specialization && (
                  <div className="flex items-center text-gray-600">
                    <Briefcase className="w-5 h-5 mr-3" />
                    <span>{profile.user.specialization}</span>
                  </div>
                )}
                {profile.user.bio && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{profile.user.bio}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{profile.stats.postCount}</p>
              <p className="text-sm text-gray-600 mt-1">게시글</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{profile.stats.likesReceived}</p>
              <p className="text-sm text-gray-600 mt-1">받은 좋아요</p>
            </div>
          </div>

          {/* Recent Posts */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">최근 게시글</h4>
            {profile.recentPosts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">작성한 게시글이 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {profile.recentPosts.map((post) => (
                  <div key={post.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        post.type === 'case_study' ? 'bg-red-100 text-red-700' :
                        post.type === 'research' ? 'bg-gray-100 text-gray-900' :
                        post.type === 'discussion' ? 'bg-green-100 text-green-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {post.type === 'case_study' ? '사례' :
                         post.type === 'research' ? '연구' :
                         post.type === 'discussion' ? '토론' : '채용'}
                      </span>
                      <span className="text-xs text-gray-500" title={post.createdAt}>
                        {formatRelativeTime(post.createdAt)}
                      </span>
                    </div>
                    <h5 className="font-semibold text-gray-900 mb-1">{post.title}</h5>
                    <p className="text-sm text-gray-600 line-clamp-2 whitespace-pre-line">{toPlainExcerpt(post.content)}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span>❤️ {post.likes}</span>
                      <span>👁 {post.views}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 계정 — 본인 프로필에서만. 탈퇴는 확인 창에서 비밀번호로 한 번 더 확인한다 */}
          {isOwnProfile && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">계정</h4>
              <p className="text-xs text-gray-500 mb-3">
                탈퇴하면 계정과 작성한 글, 올린 파일이 즉시 삭제됩니다.{' '}
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline">개인정보 처리방침</a>
              </p>
              <button
                onClick={() => setShowDeleteAccount(true)}
                className="text-sm text-red-600 hover:text-red-700 underline"
              >
                회원 탈퇴
              </button>
            </div>
          )}
        </div>
      </div>
      <DeleteAccountModal open={showDeleteAccount} onClose={() => setShowDeleteAccount(false)} />
    </div>
  );
};
